import React from 'react';
import { Avatar, Badge } from '../../../../components/ui';
import { getThemeColors } from '../../../../lib/theme';
import type { DashboardData } from '../types';

interface CallListWidgetProps {
  mode: 'full' | 'summary';
  dark: boolean;
  data: DashboardData | null;
  tr: (k: string, opts?: Record<string, unknown>) => string;
}

/**
 * Batch-13 — VBS-Dashboard: Anrufliste.
 *
 * Zeigt Anfragen, die angerufen werden müssen (s. CallListController auf
 * der API-Seite für die genaue Definition), bereits serverseitig sortiert
 * nach letztem Anrufversuch — nie angerufen ganz oben, dann am längsten
 * überfällig zuerst. Keine Client-Sortierung nötig.
 */
function formatLastCall(row: DashboardData['callList'][number], tr: (k: string, opts?: Record<string, unknown>) => string): string {
  if (row.never_called || !row.last_call_at) return tr('dashboard.widget.call_list.never_called');
  const days = Math.floor((Date.now() - new Date(row.last_call_at).getTime()) / 86400000);
  if (days <= 0) return tr('dashboard.widget.call_list.tried_today');
  return tr('dashboard.widget.call_list.days_ago', { count: days });
}

export function CallListWidget({ mode, dark, data, tr }: CallListWidgetProps) {
  const t = getThemeColors(dark);
  const items = data?.callList ?? [];
  const shown = mode === 'summary' ? items.slice(0, 3) : items;

  if (items.length === 0) {
    return <div style={{ fontSize: 12, color: t.muted }}>{data ? tr('dashboard.widget.call_list.empty') : tr('dashboard.widget.loading')}</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {shown.map((row, i) => {
        // Regression 22.09.2026: Zeilen ohne Namen (name:null) dürfen weder
        // Avatar noch Widget zum Absturz bringen → Fallback-Label.
        const label = (row.name ?? '').trim() || tr('dashboard.widget.call_list.unknown_name');
        return (
        <div key={row.id} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 0',
          borderBottom: i < shown.length - 1 ? `1px solid ${t.borderSoft}` : 'none',
        }}>
          <Avatar name={label} userId={row.id} size={32} dark={dark} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: t.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
            <div style={{ fontSize: 11, color: t.muted }}>
              {row.mobile || row.telefon || '—'}{row.ort ? ` · ${row.ort}` : ''}
            </div>
          </div>
          <Badge tone={row.never_called ? 'error' : 'warning'} dark={dark}>{formatLastCall(row, tr)}</Badge>
        </div>
        );
      })}
    </div>
  );
}
